import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { UploadSimple } from "@phosphor-icons/react";

const SECTIONS = [
  {
    key: "hero",
    title: "Hero Section",
    fields: [
      { key: "hero_headline_line1", label: "Headline Line 1", type: "text" },
      { key: "hero_headline_line2", label: "Headline Line 2", type: "text" },
      { key: "hero_subheadline", label: "Subheadline", type: "textarea" },
      { key: "hero_tag_experience", label: "Experience Tag", type: "text" },
      { key: "hero_tag_location", label: "Location Tag", type: "text" },
      { key: "hero_stat_lives", label: "Stat \u2014 Lives Transformed", type: "text" },
      { key: "hero_stat_years_label", label: "Stat \u2014 Years Label", type: "text" },
      { key: "hero_stat_rating_label", label: "Stat \u2014 Rating Label", type: "text" },
    ],
  },
  {
    key: "service_area",
    title: "Service Area Section",
    fields: [
      { key: "service_area_title", label: "Section Title", type: "text" },
      { key: "service_area_body", label: "Body Copy", type: "textarea" },
      { key: "service_area_badge_1", label: "Location Badge 1", type: "text" },
      { key: "service_area_badge_2", label: "Location Badge 2", type: "text" },
      { key: "service_area_badge_3", label: "Location Badge 3", type: "text" },
      { key: "service_area_badge_4", label: "Location Badge 4", type: "text" },
    ],
  },
  {
    key: "booking",
    title: "Booking / Consultation Section",
    fields: [
      { key: "booking_title", label: "Title", type: "text" },
      { key: "booking_subtitle", label: "Subtitle", type: "textarea" },
    ],
  },
  {
    key: "services",
    title: "Services & Packages Section",
    fields: [
      { key: "services_eyebrow", label: "Eyebrow Tag", type: "text" },
      { key: "services_title", label: "Section Title", type: "text" },
      { key: "services_group_intro_title", label: "Group 1 Title (Intro & Class Passes)", type: "text" },
      { key: "services_group_intro_subtitle", label: "Group 1 Subtitle", type: "textarea" },
      { key: "services_group_memberships_title", label: "Group 2 Title (Monthly Memberships)", type: "text" },
      { key: "services_group_memberships_subtitle", label: "Group 2 Subtitle", type: "textarea" },
      { key: "services_group_addons_title", label: "Group 3 Title (Add-Ons)", type: "text" },
      { key: "services_group_addons_subtitle", label: "Group 3 Subtitle", type: "textarea" },
      { key: "service_image_travel_addon", label: "Travel / In-Home Add-On Card Image", type: "image" },
    ],
  },
  {
    key: "quiz",
    title: "Fitness Quiz Section",
    fields: [
      { key: "quiz_eyebrow", label: "Eyebrow Tag", type: "text" },
      { key: "quiz_title", label: "Section Title", type: "text" },
      { key: "quiz_step1_question", label: "Step 1 Question", type: "text" },
      { key: "quiz_step1_subtitle", label: "Step 1 Subtitle", type: "text" },
      { key: "quiz_step2_question1", label: "Step 2 Question \u2014 Experience", type: "text" },
      { key: "quiz_step2_question2", label: "Step 2 Question \u2014 Location", type: "text" },
      { key: "quiz_step3_question", label: "Step 3 Question", type: "text" },
      { key: "quiz_success_title", label: "Success Message Title", type: "text" },
      { key: "quiz_success_body", label: "Success Message Body", type: "textarea" },
    ],
  },
  {
    key: "footer",
    title: "Footer Section",
    fields: [
      { key: "footer_tagline", label: "Tagline", type: "text" },
      { key: "footer_subtext", label: "Subtext (copyright line)", type: "text" },
    ],
  },
];

function ImageField({ value, onChange, testId }) {
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(`${process.env.REACT_APP_BACKEND_URL}/api/files/${data.file_id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <input
          data-testid={testId}
          placeholder="Image URL"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white text-sm"
        />
        <label
          data-testid={`${testId}-upload-label`}
          className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white text-xs px-4 py-2.5 rounded-full cursor-pointer transition-colors shrink-0"
        >
          <UploadSimple size={14} /> {uploading ? "..." : "Upload"}
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
      </div>
      {value && <img src={value} alt="preview" className="w-full h-32 object-cover rounded-lg mt-2" />}
    </div>
  );
}

export default function SiteContentEditor({ onSaved }) {
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/admin/site-content").then(({ data }) => {
      setSaved(data);
      setDraft(data);
    });

  useEffect(() => {
    load();
  }, []);

  if (!draft) return <p className="text-[#71717A]">Loading content...</p>;

  const isDirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const setField = (key, value) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site-content", { values: draft });
      setSaved(data);
      setDraft(data);
      toast.success("Website content updated");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const discard = () => setDraft(saved);

  return (
    <div data-testid="site-content-editor" className="space-y-8">
      {SECTIONS.map((section) => (
        <div key={section.key} data-testid={`content-section-${section.key}`} className="bg-[#141414] border border-[#27272A] rounded-2xl p-6">
          <h3 className="font-heading text-lg text-white tracking-tight mb-4">{section.title}</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            {section.fields.map((f) => (
              <div key={f.key} className={f.type === "textarea" || f.type === "image" ? "sm:col-span-2" : ""}>
                <label className="text-xs text-[#71717A] mb-1 block">{f.label}</label>
                {f.type === "textarea" ? (
                  <textarea
                    data-testid={`content-field-${f.key}`}
                    rows={2}
                    value={draft[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white text-sm"
                  />
                ) : f.type === "image" ? (
                  <ImageField testId={`content-field-${f.key}`} value={draft[f.key]} onChange={(v) => setField(f.key, v)} />
                ) : (
                  <input
                    data-testid={`content-field-${f.key}`}
                    value={draft[f.key] || ""}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-lg px-3 py-2 text-white text-sm"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="sticky bottom-0 bg-[#0A0A0A]/95 backdrop-blur border-t border-[#27272A] -mx-6 px-6 py-4 flex justify-end gap-3">
        <button
          data-testid="content-discard-button"
          disabled={!isDirty}
          onClick={discard}
          className="border border-white/20 text-white disabled:opacity-30 rounded-full px-6 py-2.5 text-sm hover:bg-white/10 transition-colors"
        >
          Discard Changes
        </button>
        <button
          data-testid="content-save-button"
          disabled={!isDirty || saving}
          onClick={save}
          className="bg-[#D4AF37] text-black disabled:opacity-30 font-semibold rounded-full px-6 py-2.5 text-sm hover:bg-white transition-colors"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}
